import { enumWithNumberValuesToArrayOfKeys, enumWithStringValuesToArrayOfKeys, } from '../types';
/** Use this to represent an empty or unknown value */
export var UnknownType;
(function (UnknownType) {
    UnknownType["MONEY"] = "common:unknownType.money";
    UnknownType["STRING"] = "common:unknownType.string";
    UnknownType["NUMBER"] = "common:unknownType.number";
})(UnknownType || (UnknownType = {}));
/** General util function to translate strings with i18next
 */
export const getTranslatedValueUtil = (t, translationPrefix, value, unknownType) => {
    if (value === null || value === undefined) {
        return t(unknownType);
    }
    // this is a conscious decision to ensure maximum compatibility
    // by falling back to the value we get from the backend
    return t(`${translationPrefix}.${String(value)}`, {
        defaultValue: value,
    });
};
/** Util to create common utils functions around an STRING enum type
 */
export function createStringEnumUtils(type, { unknownType, translationPrefix, createDefaultValue, excludeOptions, includeOptions, }) {
    const getTranslatedValue = (t, value) => getTranslatedValueUtil(t, translationPrefix, value, unknownType);
    const createOption = (t, value, disabled) => ({
        label: getTranslatedValue(t, value),
        value,
        ...(disabled !== undefined && { disabled }),
    });
    const createOptions = (t, { disabledOptions } = {}) => {
        const options = getValues().map((value) => createOption(t, value, disabledOptions?.includes(value)));
        if (includeOptions) {
            return options.filter((option) => includeOptions.includes(option.value));
        }
        if (excludeOptions) {
            return options.filter((option) => !excludeOptions.includes(option.value));
        }
        return options;
    };
    // as we export the enum and function we need to provide a getValues function!
    const getValues = () => enumWithStringValuesToArrayOfKeys(type);
    return {
        getTranslatedValue,
        createOption,
        createOptions,
        getValues,
        createDefaultValue,
    };
}
/**
 * Util to create common utils function around an NUMBER enum type
 */
export function createNumberEnumUtils(type, { unknownType, translationPrefix, createDefaultValue, }) {
    const getTranslatedValue = (t, value) => getTranslatedValueUtil(t, translationPrefix, value, unknownType);
    const createOption = (t, value) => ({
        label: getTranslatedValue(t, value),
        value,
    });
    const createOptions = (t) => getValues().map((value) => createOption(t, value));
    // as we export the enum and function we need to provide a getValues function!
    const getValues = () => enumWithNumberValuesToArrayOfKeys(type);
    return {
        getTranslatedValue,
        createOption,
        createOptions,
        getValues,
        createDefaultValue,
    };
}
export function mapUiOptions(mapFn, data) {
    return (data
        ?.map(mapFn)
        ?.filter((uiOptions) => Boolean(uiOptions.label && uiOptions.value))
        // filter duplicates
        ?.filter((uiOption, index, self) => index ===
        self.findIndex((t) => t.label === uiOption.label && t.value === uiOption.value))
        .sort((a, b) => {
        const labelA = a.label.toUpperCase();
        const labelB = b.label.toUpperCase();
        if (labelA < labelB) {
            return -1;
        }
        if (labelA > labelB) {
            return 1;
        }
        return 0;
    }) ?? []);
}
/** Util to create common utils functions around an readonly array of elements
 */
export function createArrayUtils(list, { unknownType, translationPrefix, createDefaultValue, excludeOptions, }) {
    const getTranslatedValue = (t, value) => getTranslatedValueUtil(t, translationPrefix, value, unknownType);
    const createOption = (t, value, disabled) => {
        return {
            label: getTranslatedValue(t, value),
            value,
            ...(disabled !== undefined && { disabled }),
        };
    };
    const createOptions = (t, { disabledOptions } = {}) => {
        const options = getValues().map((value) => createOption(t, value, disabledOptions?.includes(value)));
        if (excludeOptions) {
            return options.filter((option) => !excludeOptions.includes(option.value));
        }
        return options;
    };
    const valueIsPartOfArray = (value) => list.includes(value);
    // as we export the enum and function we need to provide a getValues function!
    const getValues = () => [...list];
    return {
        getTranslatedValue,
        createOption,
        createOptions,
        getValues,
        createDefaultValue,
        valueIsPartOfArray,
    };
}
